<template>
  <WdsAlert v-if="validationErrors && Object.keys(validationErrors).length" error class="tw-mb-4">
    <ul class="tw-mb-0">
      <li v-for="validationError in validationErrors" :key="validationError">
        {{ validationError }}
      </li>
    </ul>
  </WdsAlert>
  <WdsAlert v-if="displayGenericErrorMessage" error class="tw-mb-4">
    {{ $t('An error occurred') }}
  </WdsAlert>

  <div class="tw-space-y-4">
    <div class="tw-flex tw-items-center tw-gap-x-4">
      <WdsAvatar
        md
        :path="getAvatarPath(jobCandidate.worker.avatar, jobCandidate.worker.id)"
      />
      <div class="tw-flex tw-flex-col tw-gap-y-1">
        <span class="tw-inline-flex tw-items-center tw-gap-2">
          <WdsText weight="bold">{{ jobCandidate.worker.name }}</WdsText>
          <WdsTooltip v-if="workerCountryFlag">
            <template #tooltip-content>{{ workerCountryName }}</template>
            <WdsIcon
              :name="workerCountryFlag"
              :size="16"
              class="tw-flex-shrink-0"
              data-test="present-to-client-country"
            />
          </WdsTooltip>
        </span>
        <WdsTag v-if="supplierName" sm :label="supplierName" />
      </div>
      <div class="tw-ml-auto tw-pl-4 tw-border-l tw-border-gray-200 tw-self-stretch tw-flex tw-flex-col tw-justify-center">
        <WdsText size="sm" class="tw-text-gray-500">{{ $t('Client job:') }}</WdsText>
        <WdsText weight="bold">{{ clientJobName }}</WdsText>
      </div>
    </div>

    <div class="tw-flex tw-items-center tw-gap-x-4">
      <label class="tw-block tw-text-sm tw-font-medium tw-grow">{{ $t('Markup') }} *</label>
      <div class="tw-w-40">
        <WdsSelect
          :start-selected="markupBasis"
          :list="markupBasisOptions"
          data-test="present-to-client-markup-basis"
          @select-option="(opt) => (markupBasis = opt.value)"
        />
      </div>
      <div class="tw-w-40">
        <WdsInput
          v-model="markup"
          type="number"
          required
          :min="0"
          step="0.01"
          :prefix="markupInputPrefix"
          data-test="present-to-client-markup"
        />
      </div>
    </div>
    <RateCardFieldWarning
      :message="rateCardOverMaxWarning"
      data-test="present-to-client-rate-card-warning"
    />

    <div class="tw-bg-gray-50 tw-rounded-lg tw-p-4 tw-space-y-2">
      <div class="tw-flex tw-items-center tw-gap-x-2 tw-justify-between">
        <div class="tw-flex tw-items-center tw-gap-x-2">
          <WdsIcon name="dollar-sign" :size="16" class="tw-text-gray-500" />
          <WdsText weight="bold">{{ $t('Total client rate:') }}</WdsText>
        </div>
        <WdsText weight="bold">{{ currency }} {{ totalClientRate }} / {{ rateUnit }}</WdsText>
      </div>
      <div class="tw-flex tw-justify-between tw-text-sm tw-text-gray-600 tw-pl-6">
        <span>{{ $t('Worker rate:') }}</span>
        <span>{{ currency }} {{ formattedWorkerRate }} / {{ rateUnit }}</span>
      </div>
      <div v-if="!isManagedWorker" class="tw-flex tw-justify-between tw-text-sm tw-text-gray-600 tw-pl-6">
        <span>{{ $t('Staffing agency fee:') }}</span>
        <span>{{ currency }} {{ formattedReferralFee }} / {{ rateUnit }}</span>
      </div>
      <div class="tw-flex tw-justify-between tw-text-sm tw-text-gray-600 tw-pl-6">
        <span>{{ $t('Your markup:') }}</span>
        <span>{{ currency }} {{ formattedMarkupAmount }} / {{ rateUnit }}</span>
      </div>
    </div>

    <RateCardGuidanceCallout v-if="rateCardGuidance" :guidance="rateCardGuidance" :show-apply="false" />

    <div>
      <div class="tw-flex tw-items-center tw-gap-x-2 tw-mb-1">
        <span class="tw-text-sm tw-font-medium">{{ $t('Brief') }}</span>
        <WdsTag sm :label="$t('Optional')" />
      </div>
      <textarea
        v-model="brief"
        :rows="3"
        :placeholder="$t('Add message...')"
        class="tw-w-full tw-border tw-border-gray-300 tw-rounded tw-px-3 tw-py-2"
        data-test="present-to-client-brief"
      />
    </div>

    <div v-if="selectedFiles.length > 0">
      <label class="tw-block tw-text-sm tw-font-medium tw-mb-1">{{ $t('Attachments') }}</label>
      <div class="tw-space-y-1.5">
        <div
          v-for="file in selectedFiles"
          :key="file.id"
          class="tw-flex tw-items-center tw-gap-2 tw-rounded tw-bg-gray-50 tw-border tw-border-gray-200 tw-px-3 tw-py-2"
        >
          <WdsIcon name="file" :size="16" class="tw-text-gray-500" />
          <span class="tw-text-sm tw-flex-1 tw-truncate">{{ file.name }}</span>
          <WdsIcon
            name="close"
            :size="14"
            class="tw-cursor-pointer tw-text-gray-600"
            @click="removeFile(file.id)"
          />
        </div>
      </div>
    </div>

    <div v-if="selectedLinks.length > 0">
      <label class="tw-block tw-text-sm tw-font-medium tw-mb-1">{{ $t('Links') }}</label>
      <div class="tw-space-y-1.5">
        <div
          v-for="(link, index) in selectedLinks"
          :key="index"
          class="tw-flex tw-items-center tw-gap-2 tw-rounded tw-bg-gray-50 tw-border tw-border-gray-200 tw-px-3 tw-py-2"
        >
          <WdsIcon name="link" :size="16" class="tw-text-gray-500" />
          <a
            :href="link"
            target="_blank"
            rel="noopener"
            class="tw-text-sm tw-flex-1 tw-truncate tw-text-blue-600 hover:tw-underline"
          >
            {{ link }}
          </a>
          <WdsIcon
            name="close"
            :size="14"
            class="tw-cursor-pointer tw-text-gray-600"
            @click="removeLink(index)"
          />
        </div>
      </div>
    </div>

    <div class="tw-flex tw-justify-between tw-gap-sm">
      <WdsButton @click="$emit('cancel')">{{ $t('Cancel') }}</WdsButton>
      <WdsButton
        primary
        :disabled="!isValid"
        class="tw-items-center tw-gap-sm"
        data-test="present-to-client-submit"
        @click="handleSubmit"
      >
        <WdsSpinner v-if="loading" indeterminate />
        {{ $t('Present candidate') }}
      </WdsButton>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import {
  WdsAlert,
  WdsAvatar,
  WdsButton,
  WdsIcon,
  WdsInput,
  WdsSelect,
  WdsSpinner,
  WdsTag,
  WdsText,
  WdsTooltip,
} from '@worksome/wds-components'
import { useMutation } from '@vue/apollo-composable'

import RateCardFieldWarning from '@/components/Job/RateCards/RateCardFieldWarning.vue'
import RateCardGuidanceCallout from '@/components/Job/RateCards/RateCardGuidanceCallout.vue'
import { $t } from '@/app-bootstrapping/SetUpInternationalization'
import { CandidateSubmissionType, FeeApplication, FeeBasis, FeeType, JobCandidate, RateType } from '@/gql/graphql'
import { ForwardCandidateMutation } from '@/components/Job/Candidates/graphql/mutations'
import { useSupplierRateCardBand } from '@/components/Job/composables/useSupplierRateCardBand'
import { useSupplierRateCardBandGuidance } from '@/components/Job/composables/useSupplierRateCardBandGuidance'
import { durationRateTypeUnitLabels } from '@/components/Job/functions/durationRateTypeUnitLabels'
import useApolloError from '@/composables/useApolloError'
import { Notification } from '@/utils/notification'
import { getAvatarPath } from '@/utils/avatar'
import { computeFlagIcon } from '@/utils/ts-tools'

const props = defineProps<{
  jobCandidate: JobCandidate
  clientJobName?: string
  sourceJobId?: string
  rateCardsEnabled?: boolean
}>()

const emit = defineEmits(['cancel', 'candidate-presented'])

/**
 * Maps the bid's rate type to the matching duration FeeBasis so the markup
 * is expressed in the same per-unit terms (per hour, per day, ...) as the
 * worker rate. FIXED/UNKNOWN have no duration basis, so we fall back to
 * Hourly to keep the dropdown usable.
 */
const FEE_BASIS_BY_RATE_TYPE: Partial<Record<RateType, FeeBasis>> = {
  [RateType.Hourly]: FeeBasis.Hourly,
  [RateType.Daily]: FeeBasis.Daily,
  [RateType.Weekly]: FeeBasis.Weekly,
  [RateType.Monthly]: FeeBasis.Monthly,
}

const RATE_TYPE_UNIT_LABELS: Partial<Record<RateType, string>> = {
  ...durationRateTypeUnitLabels(),
  [RateType.Fixed]: $t('fixed'),
}

/**
 * Label shown in the markup-basis dropdown for the non-percentage option. We
 * surface the per-unit nature of the markup ("Per hour" / "Per day" / ...) so the
 * consultant doesn't read "Fixed" as a one-off amount. For genuinely fixed
 * (non-duration) jobs we keep "Fixed".
 */
const RATE_TYPE_DURATION_OPTION_LABELS: Partial<Record<RateType, string>> = {
  [RateType.Hourly]: $t('Per hour'),
  [RateType.Daily]: $t('Per day'),
  [RateType.Weekly]: $t('Per week'),
  [RateType.Monthly]: $t('Per month'),
  [RateType.Fixed]: $t('Fixed'),
}

const rateType = computed<RateType>(
  () => props.jobCandidate.bid?.rateType ?? RateType.Hourly,
)

const durationFeeBasis = computed<FeeBasis>(
  () => FEE_BASIS_BY_RATE_TYPE[rateType.value] ?? FeeBasis.Hourly,
)

const rateUnit = computed<string>(
  () => RATE_TYPE_UNIT_LABELS[rateType.value] ?? $t('hour'),
)

const workerCountryFlag = computed(() => {
  const isoCode = props.jobCandidate.worker.address?.country?.isoCode

  return isoCode ? computeFlagIcon(isoCode) : null
})

const workerCountryName = computed(() => props.jobCandidate.worker.address?.country?.name ?? null)

const markup = ref<string>('')
const markupBasis = ref<FeeBasis>(durationFeeBasis.value)
// Start with an empty brief: the SA's submission brief is an internal note to the
// MSP and must not bleed through to the client-facing message the MSP composes here.
const brief = ref<string>('')

const selectedFiles = ref<Array<{ id: string; name: string }>>(
  (props.jobCandidate.bid?.files ?? []).map((f) => ({ id: f.id, name: f.name })),
)

const selectedLinks = ref<string[]>([...(props.jobCandidate.bid?.links ?? [])])

const removeFile = (id: string) => {
  selectedFiles.value = selectedFiles.value.filter((f) => f.id !== id)
}

const removeLink = (index: number) => {
  selectedLinks.value.splice(index, 1)
}

const workerRate = computed(() => Number(props.jobCandidate.bid?.rate ?? 0))

/**
 * Managed workers are employed directly by the supplier, so there is no separate
 * staffing-agency referral fee to surface (the worker rate already includes the
 * supplier's margin). Referred workers do carry a referral fee on top.
 */
const isManagedWorker = computed(
  () => props.jobCandidate.bid?.workerType === CandidateSubmissionType.Managed,
)

const referralFee = computed(() => {
  if (isManagedWorker.value) {
    return 0
  }

  const fee = props.jobCandidate.bid?.fees?.find(
    f =>
      f.feeType === FeeType.Referral
      && f.basis === durationFeeBasis.value
      && f.application === FeeApplication.Additive,
  )
  return fee ? Number(fee.rate) : 0
})
const currency = computed(() => props.jobCandidate.bid?.currency ?? 'EUR')
const supplierName = computed(() => props.jobCandidate.bid?.supplier?.name ?? '')

const { band } = useSupplierRateCardBand({
  jobId: computed(() => props.sourceJobId),
  customFieldValues: computed(() => undefined),
  rate: workerRate,
  rateType,
  enabled: computed(() => (props.rateCardsEnabled ?? false) && !!props.sourceJobId),
})

// Informs the markup decision; never autofills it.
const { guidance: rateCardGuidance, fieldWarning: rateCardFieldWarning } = useSupplierRateCardBandGuidance({
  band,
  currencySymbol: currency,
  rateTypeIsUnsaved: computed(() => false),
  isClientBand: computed(() => true),
})

const rateCardOverMaxWarning = computed(() => rateCardFieldWarning(workerRate.value))

const durationMarkupLabel = computed<string>(
  () => RATE_TYPE_DURATION_OPTION_LABELS[rateType.value] ?? $t('Per hour'),
)

const markupBasisOptions = computed(() => [
  { value: durationFeeBasis.value, label: durationMarkupLabel.value },
  { value: FeeBasis.Percentage, label: $t('Percentage') },
])

const markupInputPrefix = computed(() =>
  markupBasis.value === FeeBasis.Percentage ? '%' : currency.value,
)

const baseRate = computed(() => workerRate.value + referralFee.value)

const markupAmount = computed(() => {
  const value = parseFloat(markup.value)
  if (isNaN(value)) {
    return 0
  }

  return markupBasis.value === FeeBasis.Percentage
    ? baseRate.value * (value / 100)
    : value
})

const formattedWorkerRate = computed(() => workerRate.value.toFixed(2))
const formattedReferralFee = computed(() => referralFee.value.toFixed(2))
const formattedMarkupAmount = computed(() => markupAmount.value.toFixed(2))

const totalClientRate = computed(() => (baseRate.value + markupAmount.value).toFixed(2))

const isValid = computed(() => {
  const markupValue = parseFloat(markup.value)
  return !isNaN(markupValue) && markupValue >= 0
})

const {
  loading,
  mutate: mutateForwardCandidate,
  error: errorForwardCandidate,
} = useMutation(ForwardCandidateMutation, {
  clientId: 'publicApi',
})

const { validationErrors } = useApolloError(errorForwardCandidate)
const displayGenericErrorMessage = ref(false)

const handleSubmit = async () => {
  displayGenericErrorMessage.value = false

  const markupValue = parseFloat(markup.value)
  // Percentage fees are stored as decimal fractions (e.g. 5% -> 0.05).
  const feeRate = markupBasis.value === FeeBasis.Percentage
    ? markupValue / 100
    : markupValue

  await mutateForwardCandidate({
    input: {
      sourceCandidate: props.jobCandidate.id,
      fee: {
        rate: feeRate,
        basis: markupBasis.value,
      },
      brief: brief.value || null,
      files: selectedFiles.value.map((f) => f.id),
      links: selectedLinks.value.length > 0 ? selectedLinks.value : null,
    },
  })
    .then(() => {
      new Notification({
        msg: $t('Candidate has been presented to the client.'),
      }).dispatch()
      emit('candidate-presented')
    })
    .catch(() => {
      displayGenericErrorMessage.value = true
    })
}
</script>
